import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { AdminDto, RoleDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { BaseModalProps } from '../../types/modal'
import TextInput from '../ui/TextInput'

interface AdminModalProps extends BaseModalProps {
  onSaved: (admin: AdminDto) => void
}

const toggleRoleId = (roleIds: string[], roleId: string): string[] => {
  if (roleIds.includes(roleId)) {
    return roleIds.filter(id => id !== roleId)
  }
  return [...roleIds, roleId]
}

const AdminModal = ({ isOpen, onClose, onSaved }: AdminModalProps) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [roles, setRoles] = useState<RoleDto[]>([])
  const [roleIds, setRoleIds] = useState<string[]>([])
  const [sendVerificationEmail, setSendVerificationEmail] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setFirstName('')
    setLastName('')
    setEmail('')
    setPassword('')
    setRoleIds([])
    setSendVerificationEmail(true)
    setError(null)
    setIsSaving(false)

    adminService
      .getRoles()
      .then(setRoles)
      .catch(() => setRoles([]))
  }, [isOpen])

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    password.length > 0

  const handleSubmit = async () => {
    const trimmedFirstName = firstName.trim()
    const trimmedLastName = lastName.trim()
    const trimmedEmail = email.trim()

    if (!trimmedFirstName || !trimmedLastName || !trimmedEmail || !password) {
      setError('First name, last name, email and password are required.')
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      const saved = await adminService.createAdmin({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        email: trimmedEmail,
        password,
        roleIds,
        sendVerificationEmail,
      })
      onSaved(saved)
      onClose()
    } catch {
      setError('Failed to create admin.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-popup confirm-popup role-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>Create admin</h2>
        <p style={{ marginTop: 0, opacity: 0.85, fontSize: '0.95rem' }}>
          Create a new admin account with email/password sign-in.
        </p>
        <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
          <TextInput
            required
            type="text"
            label="First name"
            name="adminFirstName"
            value={firstName}
            onChange={setFirstName}
          />
          <TextInput
            required
            type="text"
            label="Last name"
            name="adminLastName"
            value={lastName}
            onChange={setLastName}
          />
          <TextInput
            required
            type="email"
            label="Email"
            name="adminEmail"
            value={email}
            onChange={setEmail}
          />
          <TextInput
            required
            type="password"
            label="Password"
            name="adminPassword"
            value={password}
            onChange={setPassword}
          />
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={sendVerificationEmail}
                disabled={isSaving}
                onChange={e => setSendVerificationEmail(e.target.checked)}
              />{' '}
              Send verification email
            </label>
            <p style={{ marginTop: '4px', opacity: 0.7, fontSize: '0.85rem' }}>
              {sendVerificationEmail
                ? 'The admin must confirm their email before they can sign in.'
                : 'The account is created already verified and ready to sign in immediately.'}
            </p>
          </div>
          {roles.length > 0 && (
            <div className="form-group">
              <label>Roles</label>
              <div className="roles-permissions-grid">
                {roles.map(role => (
                  <label key={role.publicId} className="roles-permission-item">
                    <input
                      type="checkbox"
                      checked={roleIds.includes(role.publicId)}
                      disabled={isSaving}
                      onChange={() => setRoleIds(prev => toggleRoleId(prev, role.publicId))}
                    />
                    <span>{role.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </form>
        {error && (
          <div className="page-error" style={{ margin: 0 }}>
            {error}
          </div>
        )}
        <div className="modal-footer-content">
          <button type="button" className="styled-button" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className="styled-button"
            onClick={handleSubmit}
            disabled={isSaving || !canSubmit}
          >
            {isSaving ? 'Creating…' : 'Create admin'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default AdminModal
